import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import Cookies from 'js-cookie';
import api, { setCognitiveFlowsApiBase, setCognitiveFlowsWorkspace } from '../utils/api';
import { seedAccountRole } from '../utils/accountOwner';
// Per-TAB persistence of the selected workspace (sessionStorage truth with a
// localStorage seed for new tabs) — see utils/workspaceStorage.js for why a
// plain localStorage key leaked selections across tabs.
import { readStoredWorkspace, storeWorkspace } from '../utils/workspaceStorage';
// Shared with the Billing & Payments settings tab, which sits outside this
// provider — see utils/creditTopUp.js.
import { purchaseCredits } from '../utils/creditTopUp';
import { isCreditExhaustedError } from '../utils/apiErrors';

const CognitiveFlowsContext = createContext(null);

export function CognitiveFlowsProvider({ app_url, children }) {
    // `credits` is PURCHASED credits (what /settings has always returned).
    // `availableCredits` is what a run is actually checked against — purchased
    // plus this period's plan allowance — and is therefore what the UI shows.
    // Kept as two fields rather than one: they diverge, and a surface that
    // means "what you bought" must not silently start meaning something else.
    const [credits, setCredits] = useState(0);
    const [availableCredits, setAvailableCredits] = useState(0);
    // The account's Cognitive Agents plan, from /meta. Held HERE rather than in
    // the dashboard layout because the out-of-credits nudge has to fire on
    // canvas, chat and the workflow editor too, and those are routed as
    // SIBLINGS of that layout (see CognitiveFlowsLayout) — they never see its
    // billing summary. This provider wraps all of them.
    const [planName, setPlanName] = useState(null);
    const [planIsFree, setPlanIsFree] = useState(false);
    const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
    const [shopKey, setShopKey] = useState(null);
    const [shopDisplay, setShopDisplay] = useState('');
    const [storeError, setStoreError] = useState(null);
    const [ready, setReady] = useState(false);
    // Workspace context (from GET /meta): the account's workspaces, the acting
    // user's role/access, and the currently selected scope (null = account level).
    //
    // `role` is seeded synchronously from cookies so owner-only surfaces can
    // decide on the FIRST paint instead of flashing the owner UI at members for
    // the length of a /meta round trip. `null` = the cookies couldn't answer;
    // consumers must use `ownerResolved` rather than testing `isOwner` alone.
    // /meta still overwrites this below — the server stays authoritative.
    const [role, setRole] = useState(seedAccountRole);
    // The precise account role ('owner' | 'admin' | 'member') and the server's
    // own verdict on what this user may do. Neither can be seeded from cookies —
    // `role` above cannot tell an Owner from an Admin — so both stay null until
    // /meta lands and consumers fall back to `role === 'owner'` meanwhile.
    const [accountRole, setAccountRole] = useState(null);
    const [can, setCan] = useState(null);
    const [access, setAccess] = useState({ accountLevel: false, workspaceIds: [] });
    const [workspaces, setWorkspaces] = useState([]); // [{ id, name, createdAt }]
    // The default workspace's row ({ id: 'account', isDefault, websiteUrl,
    // default ads fields }) — null for members without the account grant.
    // Kept OUT of `workspaces`, whose ids are real ObjectIds everywhere.
    const [accountWorkspace, setAccountWorkspace] = useState(null);
    const [accountName, setAccountName] = useState('');
    const [currentWorkspaceId, setCurrentWorkspaceId] = useState(null);
    // Bumped on every scope switch — the layout keys the routed subtree on it so
    // every page remounts and refetches with the new x-cf-workspace header.
    const [workspaceEpoch, setWorkspaceEpoch] = useState(0);
    const userRole = Cookies.get('user_role') || '';

    // Set during render, not in an effect: React runs CHILD effects before
    // PARENT effects, so a routed page's mount fetches would otherwise fire
    // against the default relative base before this provider repointed it.
    // The setter is an idempotent write to module config, safe to repeat.
    if (app_url) setCognitiveFlowsApiBase(app_url);

    const refreshCredits = useCallback(async () => {
        try {
            const { data } = await api.get('/settings');
            setCredits(data.credits ?? 0);
            // Absent on a server that predates the field — fall back to
            // purchased rather than showing a wrong zero.
            setAvailableCredits(data.availableCredits ?? data.credits ?? 0);
            if (data.shop) setShopKey(data.shop);
        } catch {
            setCredits(0);
            setAvailableCredits(0);
        }
    }, []);

    const topUpCredits = useCallback(async (creditsToBuy) => {
        const data = await purchaseCredits(creditsToBuy);
        setCredits(data.credits ?? data.data?.balanceAfter ?? 0);
        // refreshCredits re-reads both from the server a line below; this only
        // keeps the visible balance from lagging a round trip behind a purchase
        // the customer just made.
        setAvailableCredits((prev) => prev + (Number(creditsToBuy) || 0));
        await refreshCredits();
        return data;
    }, [refreshCredits]);

    // Closing is public; OPENING is not. `handleCreditError` is the only way in,
    // so the modal cannot appear for a reason other than the one it explains.
    const closeUpgradeModal = useCallback(() => setUpgradeModalOpen(false), []);

    /**
     * The one place a run failure becomes an upgrade prompt.
     *
     * Returns TRUE when it has handled the error — the caller then shows
     * nothing, because the modal already said it. Returns FALSE for everything
     * else, including an out-of-credits failure on a PAID plan, which keeps its
     * existing toast: that account can top up, and a plan upsell would be the
     * wrong answer to a problem it can already solve.
     *
     * Every run surface routes its catch through this rather than testing
     * `planIsFree` itself, so the rule lives once.
     */
    const handleCreditError = useCallback((err) => {
        if (!planIsFree || !isCreditExhaustedError(err)) return false;
        setUpgradeModalOpen(true);
        return true;
    }, [planIsFree]);

    const bootstrap = useCallback(async () => {
        setStoreError(null);
        setReady(false);
        try {
            const { data: meta } = await api.get('/meta');
            const sk = meta.shop;
            const metaAccess = meta.access || { accountLevel: false, workspaceIds: [] };
            const metaWorkspaces = Array.isArray(meta.workspaces) ? meta.workspaces : [];
            const metaIsOwner = meta.role === 'owner';
            setShopKey(sk);
            setRole(meta.role || 'member');
            setAccountRole(meta.accountRole || 'member');
            setCan(meta.can || null);
            setAccess(metaAccess);
            setWorkspaces(metaWorkspaces);
            setAccountWorkspace(meta.accountWorkspace || null);
            setAccountName(meta.accountName || sk || '');
            // Absent on a server that predates this field: `undefined === true`
            // is false, so an older deployment keeps every account's Top Up
            // button and never shows the upgrade wall. The safe direction.
            setPlanName(meta.planName || null);
            setPlanIsFree(meta.planIsFree === true);

            // Restore the persisted selection, validated against what this user
            // can actually access; fall back to the account level (owners /
            // account-level members) or the first granted workspace.
            const accessibleIds = new Set(metaWorkspaces.map((w) => w.id));
            const stored = readStoredWorkspace(sk);
            let selected = stored && accessibleIds.has(stored) ? stored : null;
            if (!selected && !(metaIsOwner || metaAccess.accountLevel === true)) {
                selected = metaWorkspaces[0]?.id || null;
            }
            setCurrentWorkspaceId(selected);
            storeWorkspace(sk, selected);
            setCognitiveFlowsWorkspace(selected); // BEFORE any scoped fetch below

            const shopName = Cookies.get('shop_name');
            setShopDisplay(shopName && sk ? `${shopName} · ${sk}.myshopify.com` : sk ? `${sk}.myshopify.com` : sk);
            const { data: st } = await api.get('/settings');
            setCredits(st.credits ?? 0);
            setAvailableCredits(st.availableCredits ?? st.credits ?? 0);
        } catch (e) {
            const code = e.response?.data?.code;
            const msg = e.response?.data?.error;
            if (e.response?.status === 400 && code === 'SHOP_REQUIRED') {
                setStoreError(msg || 'Select a store in the sidebar before using Cognitive Flows.');
            } else if (e.response?.status === 403 && code === 'WORKSPACE_ACCESS_LOST') {
                setStoreError(msg || 'You no longer have access to this account — switch to another one from the sidebar.');
            }
        } finally {
            setReady(true);
        }
    }, []);

    const setCurrentWorkspace = useCallback((workspaceIdOrNull) => {
        const next = workspaceIdOrNull || null;
        setCurrentWorkspaceId(next);
        setCognitiveFlowsWorkspace(next);
        storeWorkspace(shopKey, next);
        setWorkspaceEpoch((n) => n + 1);
    }, [shopKey]);

    useEffect(() => {
        bootstrap();
    }, [bootstrap]);

    const backToApp = useCallback(() => {
        window.location.href = '/';
    }, []);

    return (
        <CognitiveFlowsContext.Provider
            value={{
                credits,
                availableCredits,
                refreshCredits,
                topUpCredits,
                planName,
                planIsFree,
                upgradeModalOpen,
                closeUpgradeModal,
                handleCreditError,
                backToApp,
                shopKey,
                shopDisplay,
                storeError,
                ready,
                userRole,
                role,
                isOwner: role === 'owner',
                // Gate owner-only surfaces on this, not on `ready`: it is true
                // from the first paint whenever the cookie seed was decisive,
                // and falls back to `ready` when it wasn't.
                ownerResolved: role !== null || ready,
                accountRole,
                isAccountAdmin: accountRole === 'admin',
                isPlatformStaff: userRole === 'admin' || userRole === 'developer',
                // The server decides these; the `role === 'owner'` fallback is
                // what keeps a new client working against a server that predates
                // the `can` block (the two deploy from separate workflows).
                canManageTeam: can ? can.manageTeam === true : role === 'owner',
                canManageWorkspaces: can ? can.manageWorkspaces === true : role === 'owner',
                canManageBilling: can ? can.manageBilling === true : role === 'owner',
                canTransferOwnership: can ? can.transferOwnership === true : role === 'owner',
                access,
                workspaces,
                accountWorkspace,
                accountName,
                currentWorkspaceId,
                setCurrentWorkspace,
                workspaceEpoch,
                // Member with no account-level view and zero granted workspaces:
                // nothing in this account is visible to them yet.
                noWorkspaceAccess: ready && role !== 'owner' && access.accountLevel !== true && workspaces.length === 0,
                appUrl: app_url || '',
                revalidateStore: bootstrap,
            }}
        >
            {children}
        </CognitiveFlowsContext.Provider>
    );
}

export function useCognitiveFlows() {
    const ctx = useContext(CognitiveFlowsContext);
    if (!ctx) {
        throw new Error('useCognitiveFlows must be used within CognitiveFlowsProvider');
    }
    return ctx;
}
